import { fetchRecentDocuments, fetchTemplatesFromBucket, AI_DRAFTING_API_BASE, apiClient } from './documentService';

// Template categories and metadata based on TEMPLATE_PATHS from documentService.js
const TEMPLATE_METADATA = {
  'Wills': [
    {
      key: 'will-pourover',
      name: 'Pour-Over Will',
      description: 'A will that transfers remaining assets to a trust upon death',
      path: 'wills/will-pourover.docx',
      category: 'Wills'
    }
  ],
  'Trusts': [
    {
      key: 'trust-disclaimer',
      name: 'Disclaimer Trust',
      description: 'A trust that allows a surviving spouse to disclaim assets for tax benefits',
      path: 'trusts/trust-disclaimer.docx',
      category: 'Trusts'
    },
    {
      key: 'revocable-trust',
      name: 'Revocable Trust',
      description: 'A flexible trust that can be modified during the grantor\'s lifetime',
      path: 'trusts/trust-disclaimer.docx',
      category: 'Trusts'
    }
  ],
  'Powers of Attorney': [
    {
      key: 'poa-general',
      name: 'General Power of Attorney',
      description: 'Grants broad powers to act on your behalf',
      path: 'powers/power-of-attorney-general.docx',
      category: 'Powers of Attorney'
    },
    {
      key: 'poa-financial',
      name: 'Financial Power of Attorney',
      description: 'Grants authority for financial decisions only',
      path: 'powers/power-of-attorney-financial.docx',
      category: 'Powers of Attorney'
    },
    {
      key: 'poa-healthcare',
      name: 'Healthcare Power of Attorney',
      description: 'Designates someone to make medical decisions on your behalf',
      path: 'powers/power-of-attorney-healthcare.docx',
      category: 'Powers of Attorney'
    }
  ],
  'Healthcare': [
    {
      key: 'hipaa-auth',
      name: 'HIPAA Authorization',
      description: 'Allows designated individuals to access your medical information',
      path: 'authorizations/HIPAA.docx',
      category: 'Healthcare'
    },
    {
      key: 'advance-directive',
      name: 'Advance Directive',
      description: 'Specifies your wishes for end-of-life medical care',
      path: 'healthcare/advance-directive.docx',
      category: 'Healthcare'
    }
  ],
  'Business': [
    {
      key: 'operating-agreement',
      name: 'Operating Agreement',
      description: 'Governs the operations of an LLC',
      path: 'business/operating-agreement.docx',
      category: 'Business'
    }
  ]
};

/**
 * Fetch template documents from S3 bucket
 */
export async function fetchTemplateDocuments() {
  try {
    // First try to fetch from the real S3 bucket
    const response = await fetchTemplatesFromBucket();
    
    if (response.error || !response.templates || response.templates.length === 0) {
      console.log('No templates from S3, falling back to hardcoded data');
      // Fallback to hardcoded templates if API fails or returns empty
      const templates = Object.values(TEMPLATE_METADATA).flat();
      
      return {
        success: true,
        templates,
        count: templates.length
      };
    }
    
    // Return the flat templates array from the API
    return {
      success: true,
      templates: response.templates || [],
      count: response.count
    };
  } catch (error) {
    console.error('Error fetching template documents:', error);
    
    // Fallback to hardcoded templates on error
    const templates = Object.values(TEMPLATE_METADATA).flat();
    
    return {
      success: true,
      templates,
      count: templates.length
    };
  }
}

/**
 * Parse document key to extract metadata
 * Example: "will-123456-20250120.docx" or "will-123456-20250120-edited.docx"
 */
export function parseDocumentKey(docKey) {
  if (!docKey) return null;
  
  try {
    // Remove .docx extension
    const keyWithoutExt = docKey.replace('.docx', '');
    
    // Check if it's edited - look for -edited pattern
    const isEdited = keyWithoutExt.includes('-edited') || keyWithoutExt.includes('edited');
    
    // Count how many times it's been edited (by counting -edited patterns)
    const editMatches = keyWithoutExt.match(/-edited/g) || [];
    const editCount = editMatches.length;
    
    // Remove all edit patterns from the key for parsing
    const cleanKey = keyWithoutExt
      .replace(/-edited-\d{8}-\d{6}/g, '')  // Remove -edited-YYYYMMDD-HHMMSS
      .replace(/-edited/g, '');             // Remove simple -edited
    
    // Split by hyphen
    const parts = cleanKey.split('-');
    
    // Extract document type (first part)
    const docType = parts[0] || 'unknown';
    
    // Extract date if present (usually in format YYYYMMDD)
    let formattedDate = null;
    
    // Look for 8-digit date pattern
    for (const part of parts) {
      if (/^\d{8}$/.test(part)) {
        // Format as YYYY-MM-DD
        formattedDate = `${part.substring(0, 4)}-${part.substring(4, 6)}-${part.substring(6, 8)}`;
        break;
      }
    }
    
    // Generate human-readable type
    const typeLabels = {
      'will': 'Will',
      'trust': 'Trust',
      'poa': 'Power of Attorney',
      'healthcare': 'Healthcare Directive',
      'hipaa': 'HIPAA Authorization',
      'advance': 'Advance Directive',
      'operating': 'Operating Agreement',
      'financial': 'Financial POA'
    };
    
    const humanType = typeLabels[docType.toLowerCase()] || docType.charAt(0).toUpperCase() + docType.slice(1);
    
    return {
      key: docKey,
      type: docType,
      humanType,
      isEdited,
      date: formattedDate,
      editCount: editCount || (isEdited ? 1 : 0)
    };
  } catch (error) {
    console.error('Error parsing document key:', error);
    return {
      key: docKey,
      type: 'unknown',
      humanType: 'Document',
      isEdited: false,
      date: null,
      editCount: 0
    };
  }
}

/**
 * Fetch documents by phase (Phase I = original, Phase II = edited)
 */
export async function fetchDocumentsByPhase(limit = 50) {
  try {
    // Use the existing fetchRecentDocuments function
    const response = await fetchRecentDocuments(limit);
    
    if (response.error) {
      throw new Error(response.error);
    }
    
    const phaseOneDocuments = [];
    const editedDocumentGroups = {};
    
    // First pass: organize documents
    response.documents.forEach(doc => {
      const parsedDoc = parseDocumentKey(doc.key);
      
      // Debug logging
      console.log(`Processing document: ${doc.key}, isEdited: ${parsedDoc.isEdited}`);
      
      // Enhance document with parsed metadata
      const enhancedDoc = {
        ...doc,
        ...parsedDoc,
        lastModified: doc.lastModified || new Date().toISOString()
      };
      
      if (parsedDoc.isEdited) {
        // Extract base document ID (remove all edit suffixes)
        const baseId = doc.key
          .replace(/-edited-\d{8}-\d{6}/g, '')  // Remove -edited-YYYYMMDD-HHMMSS
          .replace(/-edited/g, '')              // Remove simple -edited
          .replace(/\.docx$/, '');              // Remove extension for grouping
        
        if (!editedDocumentGroups[baseId]) {
          editedDocumentGroups[baseId] = {
            baseId,
            edits: []
          };
        }
        
        enhancedDoc.originalKey = baseId + '.docx';
        editedDocumentGroups[baseId].edits.push(enhancedDoc);
      } else {
        phaseOneDocuments.push(enhancedDoc);
      }
    });
    
    // Second pass: for each group, show only the latest edit with all changes
    const phaseTwoDocuments = [];
    
    await Promise.all(Object.values(editedDocumentGroups).map(async (group) => {
      // Sort by timestamp and get the latest
      const latestEdit = group.edits.sort((a, b) => 
        new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      )[0];
      
      // Add edit count
      latestEdit.editCount = group.edits.length;
      latestEdit.allVersions = group.edits.map(e => ({
        key: e.key,
        lastModified: e.lastModified
      }));
      
      // Fetch all changes for this document
      try {
        latestEdit.changes = await fetchDocumentChanges(latestEdit.key);
        console.log(`Fetched ${latestEdit.changes.length} changes for ${latestEdit.key}`);
      } catch (error) {
        console.error('Error fetching changes for', latestEdit.key, error);
        latestEdit.changes = [`Document has been edited ${latestEdit.editCount} time${latestEdit.editCount > 1 ? 's' : ''}`];
      }
      
      phaseTwoDocuments.push(latestEdit);
    }));
    
    return {
      success: true,
      phaseOne: phaseOneDocuments,
      phaseTwo: phaseTwoDocuments,
      totalCount: response.count
    };
    
  } catch (error) {
    console.error('Error fetching documents by phase:', error);
    return {
      success: false,
      phaseOne: [],
      phaseTwo: [],
      error: error.message
    };
  }
}

/**
 * Fetch real document changes from DynamoDB
 */
async function fetchDocumentChanges(docKey) {
  try {
    console.log('Fetching document changes for:', docKey);
    
    const response = await apiClient.get(`${AI_DRAFTING_API_BASE}/documents/${encodeURIComponent(docKey)}/changes`);
    const data = response.data;
    
    // Extract just the descriptions from the changes
    if (data.changes && Array.isArray(data.changes)) {
      return data.changes.map(change => change.description);
    }
    
    return ['Changes information temporarily unavailable'];
  } catch (error) {
    console.error('Error fetching document changes:', error);
    // Fallback to generic message on error
    return ['Document has been edited'];
  }
}

/**
 * Extract document changes (fallback for when API is unavailable)
 */
function extractDocumentChanges() {
  // This is now just a fallback - the real function is fetchDocumentChanges
  return ['Document has been edited'];
}

/**
 * Fetch templates from S3 (future implementation when API is available)
 * For now, this returns the same as fetchTemplateDocuments
 */
export async function fetchTemplatesFromS3() {
  // When an API endpoint is available, this would call it
  // For now, return the static template data
  return fetchTemplateDocuments();
}